import React from 'react';
import { CenteredHero, Hero } from '../blocks/hero';
import Columns from '../blocks/columns';
import Companies from '../blocks/companies';
import CtaBanner from '../blocks/cta_banner';
import Divider from '../blocks/divider';
import Cta from '../blocks/cta';
import Faq from '../blocks/faq';
import Campaign from '../blocks/campaign';
import FourSquares from '../blocks/four_squares';
import ThreeFeatures from '../blocks/three_features';
import ImageBlock from '../blocks/image';
import { LargeCard, LargeCardItems } from '../blocks/large-card';
import Numbers from '../blocks/numbers';
import PageLinks from '../blocks/page_links';
import People from '../blocks/people';
import PricingProxy from './pricing-proxy';
import ProductsBunny from '../blocks/products_bunny';
import Rating, { RatingLabeled } from '../blocks/rating/rating';
import Reviews from '../blocks/reviews';
import ReviewList from '../blocks/review_list';
import Stars from '../blocks/stars';
import FreeTrial from '../blocks/free-trial/free_trial';
import Steps from '../blocks/steps';
import { Tabs } from '../blocks/tabs';
import TabsContent from '../blocks/tabs_content';
import Testimonials from '../blocks/testimonials/testimonials';
import TestimonialsEnterprise from '../blocks/testimonials/testimonials_enterprise';
import TextWithIcon from '../blocks/text/TextWithIcon';
import TextWithImage from '../blocks/text/TextWithImage';
import Text from '../blocks/text';
import HappyCustomers from '../blocks/happy_customers';
import Logos from '../blocks/logos';
import ContactFormWrapper from '../blocks/contact_form_wrapper';
import Accordion from '../blocks/accordion';
import { VideoBlock, VideoEmbed, VideoStream } from '../blocks/video';
import Table from '../blocks/table';
import TableSimple from '../blocks/table_simple';
import Milestones from '../blocks/milestones';
import TextHighlighted from '../blocks/text/text_highlighted';
import LoadTimeComparison from '../blocks/load_time_comparison';
import StreamPriceComparison from '../blocks/stream_price_comparison';
import OriginErrors from '../blocks/origin_errors';
import TeamMap from '../blocks/team-map/TeamMap';
import StorageMap from '../blocks/storage_map';
import DynamicImageProcessing from '../blocks/dynamic_image_processing';
import PartnerLinks from '../blocks/partner_links';
import Demo from '../blocks/demo';
import Newsletter from '../blocks/newsletter';
import CardGallery from '../fragments/card-gallery';
import Multiform from '../blocks/multiform';
import SimpleBanner from '../blocks/simple_banner';
import Banner from '../blocks/banner';
import CareerCards from '../fragments/careerCards';
import Marketing from '../blocks/marketing';
import CaseStudies from '../blocks/case-studies';
import CaseStudyPanel from '../blocks/case-study-panel';
import GraphBlock from '../blocks/graph/graph-block';
// pops imports
import PopsMap from '../blocks/pops/pops_map';
import PopsMapEu from '../blocks/pops/pops_map_eu';
import PopsMapNa from '../blocks/pops/pops_map_na';
import PopsMapSa from '../blocks/pops/pops_map_sa';
import PopsMapAu from '../blocks/pops/pops_map_au';
import PopsMapAf from '../blocks/pops/pops_map_af';
import PopsMapAs from '../blocks/pops/pops_map_as';
import PopsList from '../blocks/pops/pops_list';
import PlatformCards from '../fragments/platform-cards';
import BTabs from '../blocks/btabs';
import CarouselLogo from '../blocks/carousel/carousel-logo';
import Checkmarks from '../blocks/checkmarks/checkmarks';
import IntroductionBlock from '../blocks/introduction/introduction';
import Strengths from '../blocks/strengths/strengths';
import UseCases from '../blocks/use-cases/use-cases';
import { TextWithLogosGrid } from '../blocks/logos-grid';
import TranscodingCalculator from '../blocks/transcoding-calculator/TranscodingCalculator';
import { DynamicForm } from '../blocks/dynamic-form';
import { DynamicFormReusable } from '../blocks/dynamic-form/DynamicForm';
import { SpotlightTabsBlock } from '../blocks/spotlight-tabs';
import {
  EngagementPanelBlock,
  ReusableEngagementPanel,
} from '../blocks/engagement-panel';
import { FeaturesGridBlock } from '../blocks/features-grid';
import { BlockViewportImage } from '../blocks/viewport/viewport-image';
import CloudComputeCalculator from '../blocks/calculator/CloudCompute';
import EdgeComputeComparison from '../blocks/comparison/EdgeComputeComparison';
import MixedContent from '../blocks/mixed-content/MixedContent';
import TestimonialsV2 from '../blocks/testimonials/testimonials_v2';
import NumbersV2 from '../blocks/numbers_v2';
import HopStartFormWrapper from '../forms/hop_start_wrapper';

const DefaultProxy = ({
  block,
  siteVars,
  pageClass,
  parentPage,
  isFront,
  graphData,
}: DefaultProxyProps) => {
  if (block._template.match(/^block__pricing/)) {
    return (
      <PricingProxy
        block={block}
        vars={siteVars}
      />
    );
  }

  switch (block._template) {
    case 'block__hero':
      let heroImagePositionClass = 'bg-right';
      if (block.image_position === 'Full width') {
        heroImagePositionClass = 'bg-fullwidth';
      } else if (block.image_position === 'Stick to bottom') {
        heroImagePositionClass = 'bg-bottom';
      } else if (block.image_position === 'Right Center') {
        heroImagePositionClass = 'bg-centered';
      }

      let heroTextPositionClass = 'text-left';
      if (block.text_position === 'Center') {
        heroTextPositionClass = 'text-center';
      }

      return (
        <Hero
          block={{
            ...block,
            image_position_class: heroImagePositionClass,
            text_position_class: heroTextPositionClass,
          }}
          isFront={isFront}
          parentPage={parentPage}
          vars={siteVars}
        />
      );
    case 'block__mixed_content':
      return <MixedContent block={block} />;
    case 'block__calculator_cloud_compute':
      return (
        <CloudComputeCalculator
          block={block}
          vars={siteVars}
        />
      );
    case 'block__edge_compute_comparison':
      return <EdgeComputeComparison block={block} />;
    case 'block__hero_centered':
      return <CenteredHero block={block} />;
    case 'block__viewport_image':
      return <BlockViewportImage block={block} />;
    case 'block__text_with_logos_grid':
      return <TextWithLogosGrid block={block} />;
    case 'block__divider':
      return <Divider block={block} />;
    case 'block__card_gallery':
      return <CardGallery block={block} />;
    case 'block__columns':
      return <Columns block={block} />;
    case 'block__carousel_logo':
      return (
        <CarouselLogo
          carousel_items={block.carousel_items}
          direction={block.direction}
          gap={block.gap}
          mobile_gap={block.mobile_gap}
          height={block.height}
          mobile_height={block.mobile_height}
          speed={block.speed}
        />
      );
    case 'block__companies':
      return <Companies block={block} />;
    case 'block__career_cards':
      return <CareerCards block={block} />;
    case 'block__cta_banner':
      return <CtaBanner block={block} />;
    case 'block__simple_banner':
      return <SimpleBanner block={block} />;
    case 'block__banner':
      return <Banner block={block} />;
    case 'block__graph':
      return (
        <GraphBlock
          block={block}
          graphData={graphData}
        />
      );
    case 'block__campaign':
      return <Campaign block={block} />;

    case 'block__cta':
      return (
        <Cta
          block={{
            ...block,
            page_class: pageClass,
          }}
        />
      );

    case 'block__faq':
      return <Faq block={block} />;
    case 'block__four_square_single': // Block: Four Features
      return (
        <FourSquares
          block={block}
          type='single'
        />
      );
    case 'block__four_square_group': // Block: Four Features
      return (
        <FourSquares
          block={block}
          type='filter'
        />
      );
    case 'block__four_square_horizontal': // Block: Four Features
      return (
        <FourSquares
          block={block}
          type='horizontal'
        />
      );
    case 'block__three_features':
      return <ThreeFeatures block={block} />;
    case 'block__image':
      return <ImageBlock block={block} />;
    case 'block__video':
      return (
        <VideoBlock
          url={block.url}
          max_width={block.max_width}
        />
      );
    case 'block__introduction':
      return (
        <IntroductionBlock
          pre_title={block.pre_title_style}
          title={block.title}
          text={block.text}
        />
      );
    case 'block__transcoding_calculator':
      return <TranscodingCalculator block={block} />;
    case 'block__large_card':
      return <LargeCard block={block} />;

    case 'block__large_card_items':
      return (
        <LargeCardItems
          block={block}
          vars={siteVars}
        />
      );

    case 'block__numbers':
      return (
        <Numbers
          block={block}
          siteVars={siteVars}
        />
      );
    case 'block__numbers_v2':
      return <NumbersV2 block={block} />;
      return null;
    case 'block__page_links':
      return <PageLinks block={block} />;
    case 'block__people':
      return <People block={block} />;
    case 'block__pops_map':
      return <PopsMap block={block} />;
    case 'block__pops_map_eu':
      return <PopsMapEu block={block} />;
    case 'block__pops_map_na':
      return <PopsMapNa block={block} />;
    case 'block__pops_map_latam':
      return <PopsMapSa block={block} />;
    case 'block__pops_map_au':
      return <PopsMapAu block={block} />;
    case 'block__pops_map_af':
      return <PopsMapAf block={block} />;
    case 'block__pops_map_as':
      return <PopsMapAs block={block} />;
    case 'block__pops_list':
      return <PopsList block={block} />;
    case 'block__products_bunny':
      let productsListSizeClass = 'list-large';
      if (block.list_size === 'Small') {
        productsListSizeClass = 'list-small';
      }
      return (
        <ProductsBunny
          block={{
            ...block,
            list_size_class: productsListSizeClass,
          }}
        />
      );

    case 'block__rating':
      return (
        <Rating
          block={block}
          vars={siteVars}
        />
      );
    case 'block__rating_labeled':
      return (
        <RatingLabeled
          theme={block.theme}
          title={block.title}
          trustpilot_theme={block.trustpilot_theme || 'light'}
          trustpilot_type={block.trustpilot_type || '5419b732fbfb950b10de65e5'}
          platforms={block.platforms}
          justify_content={block.justify_content}
          vars={siteVars}
        />
      );
    case 'block__reviews':
      return <Reviews block={block} />;
    case 'block__review_list':
      return (
        <ReviewList
          settings={block.settings}
          reviews={block.reviews}
          rows_no={block.rows_no}
        />
      );
    case 'block__stars':
      return (
        <Stars
          block={block}
          vars={siteVars}
        />
      );
    case 'block__strengths':
      return <Strengths strengths={block.strengths} />;
    case 'block__use_cases':
      return (
        <UseCases
          settings={block.settings}
          use_cases_theme={block.use_cases_theme}
          use_cases_padding={block.use_cases_padding}
          use_cases_text_size={block.use_cases_text_size}
          use_cases={block.use_cases}
        />
      );

    case 'block__start_free_trial':
      let trialTextPositionClass = 'pos-center';
      if (block.text_position === 'Left') {
        trialTextPositionClass = 'pos-left';
      } else if (block.text_position === 'Right') {
        trialTextPositionClass = 'pos-right';
      }
      return (
        <FreeTrial
          block={{
            ...block,
            text_position_class: trialTextPositionClass,
          }}
        />
      );

    case 'block__steps':
      return <Steps block={block} />;

    case 'block__checkmarks':
      return (
        <Checkmarks
          columns_bootstrap_rule={block.columns_bootstrap_rule}
          checkmarks={block.checkmarks}
          alignLeft={block.checkmarks_align_left}
          marginTop={block.margin_top}
          marginBottom={block.margin_bottom}
          gap={block.gap}
        />
      );

    case 'block__case_studies':
      return (
        <CaseStudies
          _template={block._template}
          css_class_name={block.css_class_name}
          case_study_grids={block.case_study_grids}
        />
      );
    case 'block__case_study_panel':
      return (
        <CaseStudyPanel
          settings={block.settings}
          case_study_panel={block.case_study_panel}
        />
      );
    case 'block__btabs':
      return (
        <BTabs
          settings={block.settings}
          btab_list={block.btab_list}
          tab_position={block.tab_position}
          show_bunnies_image={block.show_bunnies_image}
        />
      );

    case 'block__tabs':
      return <Tabs block={block} />;

    case 'block__tabs_content':
      return (
        <TabsContent
          block={block}
          vars={siteVars}
        />
      );
    case 'block__testimonials':
      return <Testimonials block={block} />;
    case 'block__testimonials_v2':
      return <TestimonialsV2 block={block} />;
    case 'block__testimonials_enterprise':
      return <TestimonialsEnterprise block={block} />;

    case 'block__text_with_icon':
      let iconPositionClass = 'icon-top';
      if (block.icon_position === 'Top Left') {
        iconPositionClass = 'icon-top-left';
      } else if (block.icon_position === 'Left') {
        iconPositionClass = 'icon-left';
      } else if (block.icon_position === 'Bottom') {
        iconPositionClass = 'icon-bottom';
      }
      return (
        <TextWithIcon
          block={{
            ...block,
            icon_position_class: iconPositionClass,
          }}
        />
      );

    case 'block__text_with_image':
      let twiImagePositionClass = 'img-right';
      if (block.image_position === 'Left') {
        twiImagePositionClass = 'img-left';
      }

      let twiStyleClass = undefined;
      if (block.style === 'Pre title arrows') {
        twiStyleClass = ' style-pretitle-arrows';
      }
      return (
        <TextWithImage
          block={{
            ...block,
            image_position_class: twiImagePositionClass,
            style_class: twiStyleClass,
          }}
        />
      );

    case 'block__text':
      return <Text block={block} />;
    case 'block__happy_customers':
      return (
        <HappyCustomers
          block={block}
          vars={siteVars}
        />
      );
    case 'block__logos':
      return <Logos block={block} />;
    case 'block__marketing':
      return <Marketing block={block} />;
    case 'block__dynamic_form':
      return <DynamicForm block={block} />;
    case 'block__dynamic_form_reusable':
      return <DynamicFormReusable block={block} />;
    case 'block__contact_form':
    case 'block__contact_form_general':
    case 'block__contact_form_abuse':
    case 'block__contact_form_get_in_touch':
    case 'block__contact_form_review':
    case 'block__contact_form_contact_sales':
    case 'block__contact_form_claim_your_gift':
    case 'block__contact_form_package':
      return <ContactFormWrapper block={block} />;
    case 'block__contact_form_hop_start':
      return <HopStartFormWrapper block={block} />;
    case 'block__contact_form_newsletter':
      return <Newsletter block={block} />;
    case 'block__multiform':
      return <Multiform block={block} />;
    case 'block__accordion':
      let accordionStyleClass =
        block?.style?.toLowerCase().replace(' ', '-') || 'style-1';

      return (
        <Accordion
          block={{
            ...block,
            style_class: accordionStyleClass,
          }}
        />
      );
    case 'block__video_embed':
      return <VideoEmbed block={block} />;
    case 'block__video_stream':
      return <VideoStream block={block} />;
    case 'block__table':
      return <Table block={block} />;
    case 'block__table_simple':
      return <TableSimple block={block} />;
    case 'block__milestones':
      return <Milestones block={block} />;
    case 'block__text_highlighted':
      return <TextHighlighted block={block} />;
    case 'block__load_time_comparison':
      return (
        <LoadTimeComparison
          block={block}
          vars={siteVars}
        />
      );
    case 'block__stream_price_comparison':
      return <StreamPriceComparison block={block} />;

    case 'block__origin_errors':
      return <OriginErrors block={block} />;
    case 'block__platform_cards':
      return <PlatformCards block={block} />;
    case 'block__team_map':
      return <TeamMap block={block} />;
    case 'block__storage_map':
      return <StorageMap block={block} />;
    case 'block__dynamic_image_processing':
      return <DynamicImageProcessing block={block} />;
    case 'block__partner_links':
      return <PartnerLinks block={block} />;
    case 'block__demo':
      return <Demo block={block} />;
    case 'block__spotlight_tabs':
      return <SpotlightTabsBlock block={block} />;
    case 'block__engagement_panel':
      return <EngagementPanelBlock block={block} />;
    case 'block__reusable_engagement_panel':
      return <ReusableEngagementPanel id={block.engagement_panel_id} />;
    case 'block__features_grid':
      return <FeaturesGridBlock block={block} />;
    default:
      return <p>Missing block.</p>;
  }
};

export default DefaultProxy;
